import { fillablePdf } from './fillable';
import { drawTitledHeader, loadAgencyLogo, AGENCY } from './agencyInfo';
import { PDFDocument, PDFName, PDFString, StandardFonts, rgb } from 'pdf-lib';

const clean = (v) => String(v ?? '');
const ascii = (v) => clean(v).replace(/[\u2013\u2014]/g, '-').replace(/[^\x20-\x7E]/g, '');

// Turns marked text boxes into drop-down lists (typing still allowed) and gives date boxes a calendar picker
const enhance = async (blob, combos, dates) => {
  const pdf = await PDFDocument.load(await blob.arrayBuffer());
  const form = pdf.getForm();
  const pages = pdf.getPages();
  const js = (code) => pdf.context.obj({ Type: 'Action', S: 'JavaScript', JS: PDFString.of(code) });
  const pt = 72 / 25.4;
  const font = await pdf.embedFont(StandardFonts.Helvetica);
  combos.forEach(({ name, page, options, x, y, w, h, value }) => {
    const pg = pages[page - 1];
    const v = ascii(value);
    const dd = form.createDropdown(name);
    dd.setOptions([...new Set([...(v ? [v] : []), '', ...options.map(ascii)])]);
    dd.enableEditing();
    if (v) dd.select(v);
    dd.addToPage(pg, { x: x * pt, y: pg.getHeight() - (y + h) * pt, width: w * pt, height: h * pt, borderWidth: 0, backgroundColor: rgb(0.93, 0.96, 1) });
    dd.setFontSize(8);
    dd.defaultUpdateAppearances(font);
  });
  dates.forEach((name) => {
    try {
      form.getTextField(name).acroField.dict.set(PDFName.of('AA'), pdf.context.obj({ K: js('AFDate_KeystrokeEx("mm/dd/yyyy");'), F: js('AFDate_FormatEx("mm/dd/yyyy");') }));
    } catch { /* field not on this copy */ }
  });
  return new Blob([await pdf.save({ updateFieldAppearances: false })], { type: 'application/pdf' });
};


// One Staff Hub PDF. mode: prefill | blank | flat | print. Every value sits in its own box so the blank copy clears it.
export const buildStaffPdf = async ({ title, mode, info = [], columns = [], rows = [], blankRows = 14, rowH = 7, totals = [], form = [], note, signatures = [], filename, landscape = true, format = 'letter', dateOptions = [] }) => {
  await loadAgencyLogo();
  const doc = fillablePdf({ orientation: landscape ? 'landscape' : 'portrait', unit: 'mm', format });
  doc.autoFill = false;
  doc.noPatientLine = true;
  const blank = mode === 'blank';
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const L = 12;
  const R = W - 12;
  const live = mode === 'prefill' || mode === 'blank';
  const combos = [];
  const dates = [];
  const pg = () => doc.getCurrentPageInfo().pageNumber;
  const fill = (x, y, w, h, value, name, extra = {}) => doc.addFill({ type: 'text', x, y, w, h, value: blank ? '' : clean(value), name, fontSize: 9.5, ...extra });
  // plain typing boxes everywhere (no drop-down arrows)
  const smart = (x, y, w, h, value, name, { date, options, ...extra } = {}) => fill(x, y, w, h, value, name, extra);

  drawTitledHeader(doc, title);
  let y = 38;
  // employee / period details, two pairs per line
  const colW = (R - L) / 2;
  info.forEach(([label, value, opts = {}], i) => {
    const x = L + (i % 2) * colW;
    if (i % 2 === 0 && i) y += 8;
    doc.setFont('helvetica', 'bold'); doc.setFontSize(10); doc.text(`${label}:`, x, y + 4.5);
    const lw = doc.getTextWidth(`${label}: `);
    doc.setDrawColor(150); doc.line(x + lw, y + 6, x + colW - 6, y + 6);
    smart(x + lw, y, colW - 6 - lw, 6, value, `info_${i}`, opts);
  });
  y += info.length ? 12 : 0;

  const header = () => {
    doc.setFillColor(17, 17, 17); doc.rect(L, y, R - L, 7, 'F');
    doc.setTextColor(212, 175, 55); doc.setFont('helvetica', 'bold'); doc.setFontSize(landscape ? 9 : 7.5);
    let x = L;
    columns.forEach((c) => { doc.text(c.h, x + 1.5, y + 4.8); x += c.w * (R - L); });
    doc.setTextColor(0);
    y += 7;
  };
  if (columns.length) {
    header();
    const list = blank ? [] : rows;
    const total = Math.max(list.length, blank ? blankRows : Math.min(4, blankRows));
    for (let r = 0; r < total; r += 1) {
      if (y > H - 30 - rowH) { doc.addPage(); y = 14; header(); }
      const row = list[r] || [];
      let x = L;
      if (row.shade) { doc.setFillColor(253, 246, 227); doc.rect(L, y, R - L, rowH, 'F'); }
      columns.forEach((c, ci) => {
        const w = c.w * (R - L);
        doc.setDrawColor(200); doc.rect(x, y, w, rowH);
        const kind = row.shade ? {} : { date: c.date, options: c.options };
        const v = row[ci];
        if (!blank && typeof v === 'string' && v.startsWith('data:image')) doc.addImage(v, 'PNG', x + 0.6, y + 0.4, w - 1.2, rowH - 0.8);
        else smart(x + 0.4, y + 0.4, w - 0.8, rowH - 0.8, v, `r${r}_c${ci}`, { fontSize: c.fs || 9, ...kind, ...(row.shade ? { shade: false } : {}) });
        x += w;
      });
      y += rowH;
    }
    y += 3;
  }
  // totals line
  if (totals.length) {
    if (y > H - 40) { doc.addPage(); y = 14; }
    const per = landscape ? totals.length : Math.min(4, totals.length);
    const tw = (R - L) / per;
    totals.forEach(([label, value], i) => {
      if (i && i % per === 0) y += 8;
      const x = L + (i % per) * tw;
      doc.setFont('helvetica', 'bold'); doc.setFontSize(landscape ? 9.5 : 8.5); doc.text(`${label}:`, x, y + 4.5);
      const lw = doc.getTextWidth(`${label}: `);
      doc.setDrawColor(120); doc.line(x + lw, y + 6, x + tw - 4, y + 6);
      fill(x + lw, y, tw - 4 - lw, 6, value, `total_${i}`, { fontSize: 10 });
    });
    y += 11;
  }
  // label / value form (time correction)
  form.forEach(([label, value, opts = {}], i) => {
    const h = opts.multiline ? 22 : 7;
    if (y + h > H - 40) { doc.addPage(); y = 14; }
    doc.setFont('helvetica', 'bold'); doc.setFontSize(10); doc.text(label, L, y + 4.8);
    doc.setDrawColor(170); doc.rect(L + 58, y, R - L - 58, h);
    if (opts.check) {
      opts.check.forEach((c, ci) => {
        const cx = L + 60 + ci * 45;
        doc.rect(cx, y + 1.5, 4, 4);
        doc.addFill({ type: 'check', x: cx, y: y + 1.5, w: 4, h: 4, value: !blank && value === c, name: `form_${i}_${ci}` });
        doc.setFont('helvetica', 'normal'); doc.text(c, cx + 6, y + 4.8);
      });
    } else fill(L + 58.5, y + 0.5, R - L - 59, h - 1, value, `form_${i}`, { multiline: Boolean(opts.multiline), fontSize: 10 });
    y += h + 2.5;
  });
  if (note) {
    y += 2;
    if (y > H - 45) { doc.addPage(); y = 14; }
    doc.setFont('helvetica', 'italic'); doc.setFontSize(8.5); doc.setTextColor(60);
    const lines = doc.splitTextToSize(note, R - L);
    doc.text(lines, L, y + 3);
    doc.setTextColor(0);
    y += lines.length * 3.8 + 3;
  }
  // signature lines: signature | printed name | date
  if (signatures.length) {
    if (y > H - (landscape ? 22 : 18) * signatures.length - 12) { doc.addPage(); y = 14; }
    y += 6;
    signatures.forEach((s, i) => {
      const sw = (R - L) * 0.45;
      const nw = Math.min(62, R - 48 - (L + sw + 8));
      doc.setDrawColor(60);
      doc.line(L, y + 10, L + sw, y + 10);
      doc.line(L + sw + 8, y + 10, L + sw + 8 + nw, y + 10);
      doc.line(R - 40, y + 10, R, y + 10);
      if (s.image && !blank) doc.addImage(s.image, 'PNG', L + 2, y - 1, 50, 10.5);
      else doc.addFill({ type: 'text', x: L, y: y + 3.5, w: sw, h: 6.3, value: '', name: `sig_${i}`, fontSize: 11 });
      fill(L + sw + 8, y + 3.5, nw, 6.3, s.name, `sig_name_${i}`, { fontSize: 10 });
      smart(R - 40, y + 3.5, 40, 6.3, s.image ? s.date : '', `sig_date_${i}`, { fontSize: 10, date: true });
      doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(80);
      doc.text(`${s.label} signature`, L, y + 14); doc.text('Printed name', L + sw + 8, y + 14); doc.text('Date', R - 40, y + 14);
      doc.setTextColor(0);
      y += landscape ? 20 : 17;
    });
  }
  const pages = doc.getNumberOfPages();
  for (let p = 1; p <= pages; p += 1) {
    doc.setPage(p);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5); doc.setTextColor(110);
    doc.text(`${AGENCY.name} · Staff Hub · ${title}`, L, H - 5);
    doc.text(`Page ${p} of ${pages}`, R, H - 5, { align: 'right' });
    doc.setTextColor(0);
  }
  if (mode === 'submit') return doc.finishBlob('flat');
  if (!live || !combos.length) { doc.fillMode = mode; doc.save(filename); return; }
  const out = await enhance(doc.finishBlob(mode), combos, dates);
  const a = document.createElement('a');
  a.href = URL.createObjectURL(out); a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 60000);
};
